import { useEffect, useRef, useState } from "react";

const SCRIPT_URL = "https://d2q3n06xhbi0am.cloudfront.net/calendar.js";
const SCRIPT_ID = "hostaway-calendar-script";

// Dedicated domain registered as Booking Engine inside Hostaway. Keeping the
// checkout on book.oonaflats.com prevents collisions with the React app on www.
const HOSTAWAY_BOOKING_BASE_URL =
  process.env.REACT_APP_HOSTAWAY_BOOKING_BASE_URL || "https://book.oonaflats.com/";

function loadScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return resolve();
    if (window.hostawayCalendarWidget) return resolve();
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * Renders Hostaway's official calendar/checkout widget.
 * Clicking "Book now" opens Hostaway's hosted checkout in a new tab.
 *
 * @param {object} props
 * @param {number|string} props.listingId - Hostaway listing ID
 * @param {string} props.lang - "de" | "en"
 */
export default function HostawayCalendarWidget({ listingId, lang = "de" }) {
  const containerRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;
    setLoadError(false);

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.hostawayCalendarWidget) return;
        // Clear any previous render before re-initializing (safe: replaceChildren removes
        // all DOM children without parsing any string as HTML — no XSS surface).
        containerRef.current.replaceChildren();
        window.hostawayCalendarWidget({
          baseUrl: HOSTAWAY_BOOKING_BASE_URL,
          listingId: Number(listingId),
          numberOfMonths: 1,
          openInNewTab: true,
          font: "Outfit",
          rounded: true,
          button: {
            action: "checkout",
            text: lang === "de" ? "Jetzt buchen" : "Book now",
          },
          clearButtonText: lang === "de" ? "Daten löschen" : "Clear dates",
          color: {
            mainColor: "#1D1D1F",
            frameColor: "#E5E5EA",
            textColor: "#1D1D1F",
          },
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    const node = containerRef.current;
    return () => {
      cancelled = true;
      if (node) node.replaceChildren();
    };
  }, [listingId, lang]);

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        id="hostaway-calendar-widget"
        data-testid="hostaway-calendar-widget"
        className="w-full"
      />
      {loadError && (
        <p data-testid="hostaway-widget-error" role="alert" className="py-6 text-center text-sm text-[#86868B]">
          {lang === "de"
            ? "Der Buchungskalender konnte nicht geladen werden. Bitte versuche es später erneut."
            : "The booking calendar could not be loaded. Please try again later."}
        </p>
      )}
    </div>
  );
}
